/** Hook for drag-to-reorder UI sections with a "lock all" toggle.
 *
 * Persists section order and lock state to localStorage.
 * Drag is disabled when `locked` is true.
 */

import { useCallback, useState } from "react";

/** Identifies a reorderable section in the main layout. */
export type SectionId =
	| "midi-input"
	| "mixer"
	| "synth-settings"
	| "scope"
	| "midi-player"
	| "sequencer";

/** Default section ordering (top-to-bottom). */
const DEFAULT_ORDER: readonly SectionId[] = [
	"midi-input",
	"mixer",
	"synth-settings",
	"scope",
	"midi-player",
	"sequencer",
] as const;

/** LocalStorage key for section order persistence. */
const STORAGE_KEY = "wasm-synth-section-order" as const;

/** Persisted shape for section order + lock flag + hidden sections. */
type PersistedSectionOrder = {
	readonly order: readonly SectionId[];
	readonly locked: boolean;
	readonly hidden: readonly SectionId[];
};

/** Load persisted order from localStorage. */
function loadOrder(): PersistedSectionOrder {
	try {
		const json = localStorage.getItem(STORAGE_KEY);
		if (!json) return { order: DEFAULT_ORDER, locked: true, hidden: [] };
		const raw = JSON.parse(json) as PersistedSectionOrder;
		if (!Array.isArray(raw.order))
			return { order: DEFAULT_ORDER, locked: true, hidden: [] };
		// Ensure all known sections are present (migration-safe)
		const known = new Set<SectionId>(DEFAULT_ORDER);
		const deduped = raw.order.filter((id): id is SectionId =>
			known.has(id as SectionId),
		);
		// Add any missing sections at the end
		for (const id of DEFAULT_ORDER) {
			if (!deduped.includes(id)) deduped.push(id);
		}
		const hidden = Array.isArray(raw.hidden)
			? raw.hidden.filter((id): id is SectionId => known.has(id as SectionId))
			: [];
		return { order: deduped, locked: raw.locked ?? true, hidden };
	} catch {
		return { order: DEFAULT_ORDER, locked: true, hidden: [] };
	}
}

/** Save order + lock to localStorage. */
function saveOrder(state: PersistedSectionOrder): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch {
		// Quota exceeded — silently ignore
	}
}

/** Return value from the useSectionOrder hook. */
export type SectionOrderAPI = {
	readonly order: readonly SectionId[];
	readonly locked: boolean;
	readonly hidden: ReadonlySet<SectionId>;
	readonly setLocked: (locked: boolean) => void;
	readonly moveSection: (from: number, to: number) => void;
	readonly hideSection: (id: SectionId) => void;
	readonly showSection: (id: SectionId) => void;
	/** Drag handlers for a section item. */
	readonly dragHandlers: (index: number) => {
		readonly draggable: boolean;
		readonly onDragStart: (e: React.DragEvent) => void;
		readonly onDragOver: (e: React.DragEvent) => void;
		readonly onDrop: (e: React.DragEvent) => void;
		readonly onDragEnd: () => void;
	};
	readonly dragOverIndex: number | null;
};

export function useSectionOrder(): SectionOrderAPI {
	const [state, setState] = useState<PersistedSectionOrder>(loadOrder);
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

	const setLocked = useCallback((locked: boolean) => {
		setState((prev) => {
			const next = { ...prev, locked };
			saveOrder(next);
			return next;
		});
	}, []);

	const hideSection = useCallback((id: SectionId) => {
		setState((prev) => {
			if (prev.hidden.includes(id)) return prev;
			const next = { ...prev, hidden: [...prev.hidden, id] };
			saveOrder(next);
			return next;
		});
	}, []);

	const showSection = useCallback((id: SectionId) => {
		setState((prev) => {
			if (!prev.hidden.includes(id)) return prev;
			const next = { ...prev, hidden: prev.hidden.filter((h) => h !== id) };
			saveOrder(next);
			return next;
		});
	}, []);

	const moveSection = useCallback((from: number, to: number) => {
		setState((prev) => {
			if (from === to) return prev;
			const arr = [...prev.order];
			const removed = arr.splice(from, 1)[0];
			if (removed === undefined) return prev;
			arr.splice(to, 0, removed);
			const next = { ...prev, order: arr };
			saveOrder(next);
			return next;
		});
	}, []);

	const dragHandlers = useCallback(
		(index: number) => ({
			draggable: !state.locked,
			onDragStart: (e: React.DragEvent) => {
				if (state.locked) {
					e.preventDefault();
					return;
				}
				setDragIndex(index);
				e.dataTransfer.effectAllowed = "move";
				e.dataTransfer.setData("text/plain", String(index));
			},
			onDragOver: (e: React.DragEvent) => {
				if (state.locked) return;
				e.preventDefault();
				e.dataTransfer.dropEffect = "move";
				setDragOverIndex(index);
			},
			onDrop: (e: React.DragEvent) => {
				if (state.locked) return;
				e.preventDefault();
				const fromIndex = dragIndex;
				if (fromIndex !== null && fromIndex !== index) {
					moveSection(fromIndex, index);
				}
				setDragIndex(null);
				setDragOverIndex(null);
			},
			onDragEnd: () => {
				setDragIndex(null);
				setDragOverIndex(null);
			},
		}),
		[state.locked, dragIndex, moveSection],
	);

	return {
		order: state.order,
		locked: state.locked,
		hidden: new Set(state.hidden),
		setLocked,
		moveSection,
		hideSection,
		showSection,
		dragHandlers,
		dragOverIndex,
	};
}
